import {
  SwapWidgetThemeVariables,
  SwapWidgetSearchParamKey,
  SwapWidgetAppTheme,
  SwapWidgetBorderRadiusSize,
  SwapWidgetThemeColorVariables
} from "../../widgetTypes";
import {omitKeys} from "../object/objectUtils";
import {WIDGET_BORDER_RADIUS_SIZE, WIDGET_THEME_COLOR_VARIABLES} from "./themeConstants";
import {NetworkToggleValue} from "../../constants";

/**
 * @returns the theme variables object (decoded) from the URL search params, or `undefined` if there is none
 */
function getThemeVarsFromUrl(): SwapWidgetThemeVariables | undefined {
  const searchParams = new URLSearchParams(document.location.search);
  const themeVarsEncoded = searchParams.get(SwapWidgetSearchParamKey.THEME_VARIABLES);
  const themeVars = themeVarsEncoded
    ? (JSON.parse(
        Buffer.from(themeVarsEncoded, "base64").toString()
      ) as SwapWidgetThemeVariables)
    : undefined;

  return themeVars;
}

/**
 * @returns the preferred network from the URL search params (if exists),
 * or `NetworkToggleValue.MainNet` as default
 */
function getPreferredNetwork(): NetworkToggleValue {
  const searchParams = new URLSearchParams(document.location.search);
  const network = searchParams.get(SwapWidgetSearchParamKey.NETWORK);

  if (
    network &&
    Object.values(NetworkToggleValue).includes(network as NetworkToggleValue)
  ) {
    return network as NetworkToggleValue;
  }

  return NetworkToggleValue.MainNet;
}

function initThemeVariables() {
  const variables = getThemeVarsFromUrl();

  const doesUserPreferDarkTheme = window.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  document.body.classList.toggle(
    "light-theme",
    variables?.theme === SwapWidgetAppTheme.Light ||
      (!variables?.theme && !doesUserPreferDarkTheme)
  );
  document.body.classList.toggle(
    "dark-theme",
    variables?.theme === SwapWidgetAppTheme.Dark ||
      (!variables?.theme && doesUserPreferDarkTheme)
  );

  initWidgetBorderRadiusSizeVariables(variables?.borderRadiusSize);

  if (variables) {
    initThemeColorVariables(
      omitKeys(
        variables,
        "title",
        "shouldDisplayTinymanLogo",
        "theme",
        "borderRadiusSize"
      )
    );
  }
}

function getPreferredTheme() {
  const variables = getThemeVarsFromUrl();

  return variables?.theme;
}

function initWidgetBorderRadiusSizeVariables(
  borderRadiusSize?: SwapWidgetBorderRadiusSize
) {
  if (borderRadiusSize) {
    Object.entries(WIDGET_BORDER_RADIUS_SIZE).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value[borderRadiusSize]);
    });
  }
}

function initThemeColorVariables(variables: SwapWidgetThemeColorVariables) {
  Object.entries(variables).forEach(([key, value]) => {
    document.documentElement.style.setProperty(
      WIDGET_THEME_COLOR_VARIABLES[key as keyof typeof WIDGET_THEME_COLOR_VARIABLES],
      value
    );
  });
}

export {getThemeVarsFromUrl, initThemeVariables, getPreferredNetwork, getPreferredTheme};
